import type { GUID } from "../types/GUID"
import { GUIDToUint8 } from "../encoding/GUIDToUint8"
import { UInt8Hash } from "./UInt8Hash"

/**
 * Computes HMAC-SHA256 message authentication code.
 *
 * Implements RFC 2104 HMAC with SHA-256 hash function.
 *
 * @param key - Secret key as Uint8Array
 * @param message - Message to authenticate as Uint8Array
 * @returns Promise resolving to GUID hash
 *
 * @example
 * ```ts
 * import { HMACSha256 } from "@pdfking/tfunc"
 *
 * const key = new Uint8Array([1, 2, 3])
 * const msg = new Uint8Array([4, 5, 6])
 * const hmac = await HMACSha256(key, msg)
 * ```
 */
export const HMACSha256 = async (
  key: Uint8Array,
  message: Uint8Array
): Promise<GUID> => {
  const blockSize = 64 // SHA-256 block size

  // If key is longer than block size, hash it first
  let processedKey = key
  if (key.length > blockSize) {
    const hashedKey = await UInt8Hash([key])
    processedKey = GUIDToUint8(hashedKey)
  }

  // Pad key to block size
  const paddedKey = new Uint8Array(blockSize)
  paddedKey.set(processedKey)

  // Create inner and outer padding
  const ipad = new Uint8Array(blockSize)
  const opad = new Uint8Array(blockSize)

  for (let i = 0; i < blockSize; i++) {
    ipad[i] = paddedKey[i] ^ 0x36
    opad[i] = paddedKey[i] ^ 0x5c
  }

  // Inner hash: hash(ipad || message)
  const innerHash = await UInt8Hash([ipad, message])
  const innerHashBytes = GUIDToUint8(innerHash)

  // Outer hash: hash(opad || innerHash)
  return await UInt8Hash([opad, innerHashBytes])
}
